import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminFetch, qs, rupiah } from './api';
import { ErrorBox, btnPrimary, btnSecondary, inputClass } from './ui';
import BottomSheet from '../components/BottomSheet';
import { ProductPicker, summarizeSelection } from './ProductPicker';
import { useAdminSummary } from './AdminLayout';
import RegionCombobox from '../shop/RegionCombobox';
import { REGION_LEVELS } from '../shop/regionsApi';
import { EMPTY_REGION, Field, inputCls, useRegionList, validateCheckoutForm } from '../shop/Checkout';
import { regionLine } from '../shop/SavedAddresses';
import { newIdempotencyKey } from '../shop/format';
import { normalizePhone } from '../phone';

// Halaman admin "Buat pesanan" (/admin/orders/new): admin membuat pesanan ATAS NAMA pelanggan terdaftar atau
// pelanggan tanpa akun. Urutan (HP): Pelanggan -> Alamat -> Produk -> Catatan -> bar bawah menetap (ringkasan +
// "Buat pesanan") -> sheet konfirmasi -> POST /api/admin/orders -> detail pesanan.
// Harga di layar hanya PERKIRAAN (harga & jenjang produk yang dimuat); total final dihitung server.

export const GuestBadge = ({ className = '' }) => (
  <span
    className={`inline-block whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 ${className}`}
    data-testid="guest-badge"
  >
    Tanpa akun
  </span>
);

const CloseX = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Tutup"
    className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
  >
    ×
  </button>
);

const customerLabel = (c) => (c?.alias ? `${c.alias} (${c.name})` : c?.name || '');

// ---------- Pilih / tambah pelanggan ----------

export const CustomerPickerSheet = ({ open, onClose, onPick }) => {
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [state, setState] = useState({ items: [], loading: true, error: null });
  const [mode, setMode] = useState('list');
  const [form, setForm] = useState({ name: '', phone: '', alias: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [existing, setExisting] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(draft.trim()), 300);
    return () => clearTimeout(t);
  }, [draft]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    adminFetch(`/customers${qs({ q, per_page: 20 })}`)
      .then((r) => alive && setState({ items: r?.items || [], loading: false, error: null }))
      .catch((err) => alive && setState({ items: [], loading: false, error: err }));
    return () => {
      alive = false;
    };
  }, [q, open]);
  useEffect(() => {
    if (!open) {
      setMode('list');
      setError(null);
      setExisting(null);
    }
  }, [open]);

  const save = async (e) => {
    e?.preventDefault();
    if (savingRef.current) return;
    const name = form.name.trim();
    if (!name) return setError(new Error('Nama pelanggan wajib diisi.'));
    const ph = normalizePhone(form.phone);
    if (ph.error) return setError(new Error(ph.error));
    savingRef.current = true;
    setSaving(true);
    setError(null);
    setExisting(null);
    try {
      const c = await adminFetch('/customers', { method: 'POST', body: { name, phone: form.phone.trim(), alias: form.alias.trim() || null } });
      setForm({ name: '', phone: '', alias: '' });
      onPick(c);
    } catch (err) {
      if (err.status === 409 && err.data?.existing) setExisting(err.data.existing);
      setError(err);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    return undefined;
  };

  const header = (
    <>
      <div className="flex items-start justify-between gap-3">
        <h2 id="customer-picker-title" className="text-lg font-bold text-gray-900">
          {mode === 'list' ? 'Pilih pelanggan' : 'Tambah pelanggan tanpa akun'}
        </h2>
        <CloseX onClick={onClose} />
      </div>
      {mode === 'list' && (
        <input
          type="search"
          className={`${inputClass} mb-2 mt-2 text-base`}
          placeholder="Cari alias, nama, telepon, email"
          value={draft}
          maxLength={100}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Cari pelanggan"
          data-testid="customer-search"
        />
      )}
    </>
  );
  const footer =
    mode === 'list' ? (
      <button
        type="button"
        onClick={() => {
          setMode('new');
          setError(null);
          setExisting(null);
        }}
        className="min-h-[44px] w-full rounded-lg border border-purple-700 bg-white px-4 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50"
      >
        Tambah pelanggan tanpa akun
      </button>
    ) : (
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" className={btnSecondary} onClick={() => setMode('list')} disabled={saving}>
          Kembali
        </button>
        <button type="submit" form="guest-form" className={btnPrimary} disabled={saving}>
          {saving ? 'Menyimpan...' : 'Simpan pelanggan'}
        </button>
      </div>
    );

  return (
    <BottomSheet open={open} onClose={onClose} labelledBy="customer-picker-title" testId="customer-picker" header={header} footer={footer}>
      {mode === 'list' ? (
        <>
          {state.error && <ErrorBox error={state.error} />}
          {state.loading && state.items.length === 0 && <p className="py-4 text-center text-sm text-gray-500">Memuat...</p>}
          {!state.loading && !state.error && state.items.length === 0 && (
            <p className="py-4 text-center text-sm text-gray-500">Pelanggan tidak ditemukan. Tambahkan pelanggan tanpa akun bila perlu.</p>
          )}
          <ul className="divide-y divide-gray-100">
            {state.items.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="flex w-full items-start justify-between gap-2 py-3 text-left hover:bg-purple-50"
                  onClick={() => onPick(c)}
                  data-testid="customer-option"
                >
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {c.alias && <span className="block font-semibold text-purple-800">{c.alias}</span>}
                    <span className={c.alias ? 'block text-xs text-gray-600' : 'block font-medium text-gray-900'}>{c.name}</span>
                    <span className="block text-xs text-gray-500">
                      {[c.phone, c.accountType === 'guest' ? '' : c.email].filter(Boolean).join(' · ') || '-'}
                    </span>
                  </span>
                  {c.accountType === 'guest' && <GuestBadge className="shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <form id="guest-form" onSubmit={save} noValidate className="space-y-3 pb-2">
          <p className="text-sm text-gray-600">Untuk pelanggan yang minta dibuatkan pesanan. Pelanggan ini tidak bisa login dan hanya terlihat oleh admin.</p>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Nama (wajib)</span>
            <input className={`${inputClass} text-base`} maxLength={100} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="guest-name" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Telepon/WhatsApp (opsional)</span>
            <input
              className={`${inputClass} text-base`}
              inputMode="tel"
              maxLength={32}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="guest-phone"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Alias (opsional, hanya admin)</span>
            <input className={`${inputClass} text-base`} maxLength={100} value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} data-testid="guest-alias" />
          </label>
          {error && <ErrorBox error={error} />}
          {existing && (
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm" data-testid="guest-existing">
              <p className="text-gray-800">
                Sudah ada: <strong>{customerLabel(existing)}</strong> {existing.accountType === 'guest' ? <GuestBadge /> : '(akun terdaftar)'}
              </p>
              <button type="button" className={`${btnPrimary} mt-2`} onClick={() => onPick(existing)}>
                Pakai pelanggan ini
              </button>
            </div>
          )}
        </form>
      )}
    </BottomSheet>
  );
};

// ---------- Alamat ----------

const AdminAddressSheet = ({ open, onClose, addresses, onPick, onNew }) => (
  <BottomSheet
    open={open}
    onClose={onClose}
    labelledBy="admin-address-title"
    testId="admin-address-picker"
    header={
      <div className="flex items-start justify-between gap-3">
        <h2 id="admin-address-title" className="text-lg font-bold text-gray-900">
          Pilih alamat
        </h2>
        <CloseX onClick={onClose} />
      </div>
    }
    footer={
      <button
        type="button"
        onClick={onNew}
        className="min-h-[44px] w-full rounded-lg border border-purple-700 bg-white px-4 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50"
      >
        Tambah alamat baru
      </button>
    }
  >
    <ul className="mt-1 space-y-3">
      {addresses.map((a) => (
        <li key={a.id} className="rounded-lg border border-gray-200 p-3" data-testid="admin-address-card">
          <p className="font-semibold text-gray-900 break-words">{a.recipientName}</p>
          <p className="text-sm text-gray-600">{a.recipientPhone}</p>
          <p className="mt-1 text-sm text-gray-700 break-words">{a.address}</p>
          <p className="text-xs text-gray-500 break-words">
            {regionLine(a)}
            {a.postalCode ? ` ${a.postalCode}` : ''}
          </p>
          {a.regionComplete ? (
            <button type="button" onClick={() => onPick(a)} className={`${btnPrimary} mt-2`}>
              Pilih alamat
            </button>
          ) : (
            <p className="mt-1 text-xs text-amber-800">Wilayah alamat lama ini belum lengkap. Gunakan “Tambah alamat baru”.</p>
          )}
        </li>
      ))}
    </ul>
  </BottomSheet>
);

export const emptyAddressForm = (phone = '') => ({ recipientName: '', recipientPhone: phone || '', address: '', postalCode: '', note: '' });

// Form alamat lengkap (komponen & aturan validasi yang sama dengan checkout pelanggan).
const AddressForm = ({ form, setForm, region, setRegion, errors }) => {
  const lists = [
    useRegionList(0, null),
    useRegionList(1, region.province?.code),
    useRegionList(2, region.regency?.code),
    useRegionList(3, region.district?.code),
  ];
  const set = (k) => (e) => {
    let v = e.target.value;
    if (k === 'postalCode') v = v.replace(/[^0-9]/g, '').slice(0, 5);
    setForm((f) => ({ ...f, [k]: v }));
  };
  const pickRegion = (level) => (item) => {
    const key = REGION_LEVELS[level].key;
    setRegion((r) => {
      if (r[key]?.code === item.code) return r;
      const next = { ...r, [key]: item };
      REGION_LEVELS.slice(level + 1).forEach((lv) => {
        next[lv.key] = null;
      });
      return next;
    });
  };
  return (
    <div className="space-y-4" data-testid="admin-address-form">
      <Field id="co-name" label="Nama penerima" required error={errors.recipientName}>
        <input id="co-name" className={inputCls(errors.recipientName)} maxLength={100} value={form.recipientName} onChange={set('recipientName')} />
      </Field>
      <Field id="co-phone" label="Nomor telepon/WhatsApp" hint="(08xx / +628xx)" required error={errors.recipientPhone}>
        <input id="co-phone" className={inputCls(errors.recipientPhone)} inputMode="tel" maxLength={32} value={form.recipientPhone} onChange={set('recipientPhone')} />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {REGION_LEVELS.map((lv, i) => {
          const parent = i > 0 ? REGION_LEVELS[i - 1] : null;
          return (
            <RegionCombobox
              key={lv.key}
              id={`co-${lv.key}`}
              label={lv.label}
              lower={lv.lower}
              value={region[lv.key]}
              items={lists[i].items}
              loading={lists[i].loading}
              error={lists[i].error}
              onRetry={lists[i].retry}
              onChange={pickRegion(i)}
              disabled={!!parent && !region[parent.key]}
              disabledHint={parent ? `Pilih ${parent.lower} dulu` : ''}
              invalid={!!errors[lv.field]}
              errorText={errors[lv.field]}
            />
          );
        })}
      </div>
      <Field id="co-address" label="Alamat lengkap" hint="(jalan, RT/RW, nomor rumah)" required error={errors.address}>
        <textarea id="co-address" className={inputCls(errors.address)} rows={3} maxLength={500} value={form.address} onChange={set('address')} />
      </Field>
      <Field id="co-postal" label="Kode pos" hint="(5 digit)" required error={errors.postalCode}>
        <input id="co-postal" className={inputCls(errors.postalCode)} inputMode="numeric" maxLength={5} value={form.postalCode} onChange={set('postalCode')} />
      </Field>
    </div>
  );
};

// ---------- Ringkasan & konfirmasi ----------

export const addressPayload = (mode, saved, form, region) =>
  mode === 'saved'
    ? { addressId: saved.id }
    : {
        recipientName: form.recipientName.trim(),
        recipientPhone: form.recipientPhone.trim(),
        address: form.address.trim(),
        postalCode: form.postalCode.trim(),
        provinceCode: region.province?.code || '',
        regencyCode: region.regency?.code || '',
        districtCode: region.district?.code || '',
        villageCode: region.village?.code || '',
      };

const regionNames = (region) =>
  [region.village?.name, region.district?.name ? `Kec. ${region.district.name}` : '', region.regency?.name, region.province?.name].filter(Boolean).join(', ');

export const SectionTitle = ({ n, children }) => (
  <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-800">
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-800">{n}</span>
    {children}
  </h2>
);

const Section = ({ children, testId }) => (
  <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm" data-testid={testId}>
    {children}
  </section>
);

// ---------- Halaman ----------

export const AdminOrderCreate = () => {
  const navigate = useNavigate();
  const { refresh: refreshSummary } = useAdminSummary();
  const [customer, setCustomer] = useState(null);
  const [custOpen, setCustOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addrState, setAddrState] = useState({ loading: false, error: null });
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrMode, setAddrMode] = useState(null); // 'saved' | 'new' | null
  const [savedAddr, setSavedAddr] = useState(null);
  const [form, setForm] = useState(emptyAddressForm());
  const [region, setRegion] = useState(EMPTY_REGION);
  const [items, setItems] = useState([]);
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);
  const doneRef = useRef(false);
  // Satu kunci idempotensi per sesi halaman: klik ganda / kirim ulang -> pesanan yang sama.
  const idemKey = useRef(newIdempotencyKey());

  const dirty = !!customer || items.length > 0 || note.trim() !== '';
  useEffect(() => {
    if (!dirty) return undefined;
    const onBefore = (e) => {
      if (doneRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [dirty]);

  const loadAddresses = (c) => {
    setAddrState({ loading: true, error: null });
    adminFetch(`/customers/${c.id}/addresses`)
      .then((r) => {
        setAddresses(r?.items || []);
        setAddrState({ loading: false, error: null });
      })
      .catch((err) => {
        setAddresses([]);
        setAddrState({ loading: false, error: err });
      });
  };
  const pickCustomer = (c) => {
    setCustomer(c);
    setCustOpen(false);
    setAddrMode(null);
    setSavedAddr(null);
    setForm(emptyAddressForm(c.phone));
    setRegion(EMPTY_REGION);
    loadAddresses(c);
  };
  const newAddress = () => {
    setAddrOpen(false);
    setAddrMode('new');
    setSavedAddr(null);
    setForm(emptyAddressForm(customer?.phone));
    setRegion(EMPTY_REGION);
  };

  const formErrors = useMemo(() => (addrMode === 'new' ? validateCheckoutForm(form, region) : {}), [addrMode, form, region]);
  // Galat ditampilkan untuk kolom yang SUDAH diisi tetapi tidak valid (kolom kosong cukup lewat petunjuk
  // "Lengkapi ..." di bar bawah; tombol Buat pesanan nonaktif sampai alamat valid).
  const visibleErrors = useMemo(
    () => Object.fromEntries(Object.entries(formErrors).filter(([k]) => String(form[k] ?? '').trim() !== '')),
    [formErrors, form],
  );
  const addressReady = (addrMode === 'saved' && !!savedAddr) || (addrMode === 'new' && Object.keys(formErrors).length === 0);
  const ready = !!customer && addressReady && items.length > 0 && items.every((l) => !l.unavailable);
  const sum = summarizeSelection(items);

  const missing = [!customer && 'pelanggan', !addressReady && 'alamat', items.length === 0 && 'produk'].filter(Boolean);
  const openConfirm = () => {
    if (!ready) return;
    setError(null);
    setConfirmOpen(true);
  };
  const submit = async () => {
    if (inFlight.current) return; // cegah klik ganda
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminFetch('/orders', {
        method: 'POST',
        body: {
          customerId: customer.id,
          address: addressPayload(addrMode, savedAddr, form, region),
          items: items.map((l) => ({ productId: l.productId, qty: l.qty })),
          note: note.trim(),
          idempotencyKey: idemKey.current,
        },
      });
      doneRef.current = true;
      refreshSummary();
      navigate(`/admin/orders/${res.id}`);
    } catch (err) {
      inFlight.current = false;
      setSubmitting(false);
      setError(err);
    }
  };

  const addrSummary =
    addrMode === 'saved' && savedAddr
      ? {
          name: savedAddr.recipientName,
          phone: savedAddr.recipientPhone,
          address: savedAddr.address,
          region: `${regionLine(savedAddr)}${savedAddr.postalCode ? ` ${savedAddr.postalCode}` : ''}`,
        }
      : { name: form.recipientName, phone: form.recipientPhone, address: form.address, region: `${regionNames(region)} ${form.postalCode}`.trim() };

  return (
    <div className="space-y-4 pb-32">
      <div>
        <Link to="/admin/orders" className="text-sm text-purple-700 hover:underline">
          ‹ Semua pesanan
        </Link>
        <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">Buat pesanan</h1>
        <p className="text-sm text-gray-500">Pesanan atas nama pelanggan. Harga dihitung ulang oleh server dengan harga terbaru.</p>
      </div>

      <Section testId="section-customer">
        <SectionTitle n={1}>Pelanggan</SectionTitle>
        {customer ? (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-purple-200 bg-purple-50/40 p-3" data-testid="selected-customer">
            <div className="min-w-0 [overflow-wrap:anywhere]">
              <p className="font-semibold text-gray-900">
                {customerLabel(customer)} {customer.accountType === 'guest' && <GuestBadge />}
              </p>
              <p className="text-sm text-gray-600">{customer.phone || '-'}</p>
            </div>
            <button type="button" className="shrink-0 text-sm font-medium text-purple-700 underline" onClick={() => setCustOpen(true)}>
              Ganti
            </button>
          </div>
        ) : (
          <button type="button" className={`${btnPrimary} w-full sm:w-auto`} onClick={() => setCustOpen(true)}>
            Pilih pelanggan
          </button>
        )}
      </Section>

      <Section testId="section-address">
        <SectionTitle n={2}>Alamat pengiriman</SectionTitle>
        {!customer ? (
          <p className="text-sm text-gray-500">Pilih pelanggan terlebih dahulu.</p>
        ) : (
          <div className="space-y-3">
            {addrState.error && <ErrorBox error={addrState.error} />}
            {addrState.loading && <p className="text-sm text-gray-500">Memuat alamat...</p>}
            {addrMode === 'saved' && savedAddr && (
              <div className="rounded-xl border-2 border-purple-200 bg-purple-50/40 p-3" data-testid="admin-selected-address">
                <p className="font-semibold text-gray-900 break-words">{savedAddr.recipientName}</p>
                <p className="text-sm text-gray-600">{savedAddr.recipientPhone}</p>
                <p className="mt-1 text-sm text-gray-800 break-words">{savedAddr.address}</p>
                <p className="text-sm text-gray-600 break-words">{addrSummary.region}</p>
              </div>
            )}
            {addrMode === 'new' && (
              <AddressForm
                form={form}
                setForm={setForm}
                region={region}
                setRegion={setRegion}
                errors={visibleErrors}
              />
            )}
            {!addrState.loading && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {addresses.length > 0 && (
                  <button type="button" className={btnPrimary} onClick={() => setAddrOpen(true)}>
                    {addrMode === 'saved' ? 'Ganti alamat' : 'Pilih alamat'}
                  </button>
                )}
                {addrMode !== 'new' && (
                  <button type="button" className={btnSecondary} onClick={newAddress}>
                    Tambah alamat baru
                  </button>
                )}
              </div>
            )}
            {addrMode === 'new' && <p className="text-xs text-gray-500">Alamat baru disimpan ke alamat pelanggan ini (bukan sebagai default).</p>}
          </div>
        )}
      </Section>

      <Section testId="section-products">
        <SectionTitle n={3}>Produk</SectionTitle>
        <ProductPicker value={items} onChange={setItems} />
      </Section>

      <Section testId="section-note">
        <SectionTitle n={4}>Catatan pesanan (opsional)</SectionTitle>
        <textarea className={`${inputClass} text-base`} rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Catatan pesanan" />
      </Section>

      {/* Bar bawah menetap (kanan sidebar admin). */}
      <div
        className="fixed bottom-0 left-16 right-0 z-20 border-t border-gray-200 bg-white px-4 pt-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] lg:left-[260px]"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        data-testid="create-bottom-bar"
      >
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <div className="min-w-0 text-sm">
            <div className="text-gray-600" data-testid="create-summary-count">
              {sum.lines} produk · {sum.items} item
            </div>
            <div className="font-bold text-purple-700" data-testid="create-summary-total">
              {rupiah(sum.subtotal)} <span className="text-xs font-normal text-gray-500">sementara</span>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <button type="button" className={btnPrimary} disabled={!ready} onClick={openConfirm} data-testid="create-submit">
              Buat pesanan
            </button>
            {missing.length > 0 && (
              <span className="text-[11px] text-gray-500" data-testid="create-missing">
                Lengkapi {missing.join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>

      <CustomerPickerSheet open={custOpen} onClose={() => setCustOpen(false)} onPick={pickCustomer} />
      <AdminAddressSheet
        open={addrOpen}
        onClose={() => setAddrOpen(false)}
        addresses={addresses}
        onNew={newAddress}
        onPick={(a) => {
          setSavedAddr(a);
          setAddrMode('saved');
          setAddrOpen(false);
        }}
      />
      <BottomSheet
        open={confirmOpen}
        onClose={() => !inFlight.current && setConfirmOpen(false)}
        labelledBy="create-confirm-title"
        testId="create-confirm"
        header={
          <h2 id="create-confirm-title" className="text-lg font-bold text-gray-900">
            Buat pesanan ini?
          </h2>
        }
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className={btnSecondary} onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Batal
            </button>
            <button type="button" className={btnPrimary} onClick={submit} disabled={submitting} data-testid="create-confirm-yes">
              {submitting ? 'Membuat...' : 'Ya, buat pesanan'}
            </button>
          </div>
        }
      >
        {customer && (
          <div className="space-y-3 pb-2 text-sm">
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500">Pelanggan</div>
              <div className="font-medium text-gray-900">
                {customerLabel(customer)} {customer.accountType === 'guest' && <GuestBadge />}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500">Alamat</div>
              <div className="text-gray-900 [overflow-wrap:anywhere]">
                {addrSummary.name} · {addrSummary.phone}
                <br />
                {addrSummary.address}
                <br />
                <span className="text-gray-600">{addrSummary.region}</span>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500">Produk</div>
              <ul className="mt-1 space-y-1" data-testid="create-confirm-items">
                {items.map((l) => (
                  <li key={l.productId} className="flex justify-between gap-2">
                    <span className="min-w-0 [overflow-wrap:anywhere]">{l.name}</span>
                    <span className="shrink-0 font-semibold">× {l.qty}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-between border-t pt-2 font-bold text-purple-700">
              <span>Total sementara</span>
              <span>{rupiah(sum.subtotal)}</span>
            </div>
            <p className="rounded-md bg-orange-50 px-3 py-2 text-orange-900">Pesanan akan berstatus Menunggu konfirmasi (ongkir dikonfirmasi setelahnya).</p>
            {error && <ErrorBox error={error} />}
          </div>
        )}
      </BottomSheet>
    </div>
  );
};

export default AdminOrderCreate;
